import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./EmailVerificationPage.css";

/* ===== Base API robuste (prod & dev) ===== */
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";
const API_BASE =
  RAW
    ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
    : (typeof window !== "undefined" && window.location.origin) || "";

const VERIFY_EMAIL_STORAGE_KEY = "pendingVerifyEmail";
const COOLDOWN_SEC = 30;

function resolveEmailFromEntry(location) {
  const fromState = location.state?.email;
  if (typeof fromState === "string" && fromState.trim()) return fromState.trim().toLowerCase();
  try {
    const q = new URLSearchParams(location.search || "").get("email");
    if (q && q.trim()) return decodeURIComponent(q.trim()).toLowerCase();
  } catch {
    /* ignore */
  }
  try {
    const stored = sessionStorage.getItem(VERIFY_EMAIL_STORAGE_KEY);
    if (stored && stored.trim()) return stored.trim().toLowerCase();
  } catch {
    /* ignore */
  }
  return "";
}

function EmailVerificationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [emailLocked, setEmailLocked] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [timer, setTimer] = useState(COOLDOWN_SEC);

  useEffect(() => {
    const e = resolveEmailFromEntry(location);
    setEmailLocked(e);
    if (e) {
      try {
        sessionStorage.setItem(VERIFY_EMAIL_STORAGE_KEY, e);
      } catch {
        /* ignore */
      }
    }
  }, [location.state, location.search, location.key]);

  const safeResend = useCallback(async (emailToUse) => {
    const email = (emailToUse || "").trim();
    if (!email) return false;
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (res.ok) return true;
      try {
        const data = await res.json();
        setMsg(`❌ ${data?.error || "Impossible de renvoyer le code."}`);
      } catch {
        setMsg("❌ Impossible de renvoyer le code.");
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const startCooldown = useCallback(() => {
    setIsResendDisabled(true);
    setTimer(COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const email = resolveEmailFromEntry(location);
    const autoSend = location.state?.startCooldown === true;

    startCooldown();

    if (autoSend && email) {
      (async () => {
        const ok = await safeResend(email);
        if (cancelled) return;
        setMsg(
          ok
            ? "✅ Code envoyé par email."
            : "❌ Impossible d’envoyer le code automatiquement. Réessayez avec « Renvoyer le code » après le délai."
        );
      })();
    }

    return () => {
      cancelled = true;
    };
    // Intentionnel : une seule exécution à l’entrée sur la page (rechargement = nouveau délai 30 s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isResendDisabled) return;
    if (timer <= 0) {
      setIsResendDisabled(false);
      return;
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer, isResendDisabled]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!emailLocked.trim()) {
      setMsg("❌ Adresse email introuvable. Revenez à l’inscription.");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: emailLocked.trim(), code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Vérification impossible");

      try {
        sessionStorage.removeItem(VERIFY_EMAIL_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setMsg(`✅ ${data.message || "Email vérifié avec succès."}`);
      setTimeout(() => navigate("/auth/login"), 1500);
    } catch (err) {
      setMsg(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!emailLocked.trim() || isResendDisabled) return;
    setMsg("");
    const ok = await safeResend(emailLocked);
    if (ok) {
      setMsg("✅ Code renvoyé par email.");
      startCooldown();
    }
  };

  return (
    <div className="email-verification-container">
      <h2 className="ev-title">Vérification de votre email</h2>
      <p className="ev-subtitle">
        Un code vous a été envoyé (vérifiez aussi vos courriers indésirables / spam).
      </p>

      <div className="ev-email-block">
        <label className="ev-email-label" htmlFor="ev-email-readonly">
          Adresse e-mail
        </label>
        <output
          id="ev-email-readonly"
          className="ev-email-readonly"
          aria-readonly="true"
        >
          {emailLocked || "—"}
        </output>
        {!emailLocked && (
          <p className="ev-email-missing">
            Si vous venez de vous inscrire, repassez par la page d’inscription ou utilisez le lien reçu par mail.
          </p>
        )}
      </div>

      <form className="ev-form" onSubmit={handleVerify}>
        <label className="ev-code-label" htmlFor="ev-code-input">
          Code à 6 chiffres
        </label>
        <input
          id="ev-code-input"
          className="ev-input"
          type="text"
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          required
        />

        <button className="ev-button" type="submit" disabled={loading || !emailLocked}>
          {loading ? "Vérification…" : "Vérifier"}
        </button>

        <button
          className="ev-resend"
          type="button"
          onClick={handleResendCode}
          disabled={!emailLocked || isResendDisabled}
          title={
            isResendDisabled
              ? `Vous pourrez renvoyer un code dans ${timer}s`
              : "Renvoyer le code par email"
          }
        >
          {isResendDisabled ? `Renvoyer le code (${timer}s)` : "Renvoyer le code"}
        </button>
      </form>

      <p className="ev-hint" role="note" aria-live="polite">
        Merci de vérifier vos spams / indésirables pour les e-mails{" "}
        <strong>ProMeetService</strong>.
      </p>

      {msg ? <p className={`ev-message ${msg.startsWith("✅") ? "success" : "error"}`}>{msg}</p> : null}
    </div>
  );
}

export default EmailVerificationPage;
